
import { computed, defineComponent, reactive, ref, watch, onMounted } from 'vue';
import { datetimeFormat } from '@/utils';
import dayjs from 'dayjs';
import DialogCustomerCouponDetail from '@/components/organisms/coupon/DialogCustomerCouponDetail.vue';
import SimpleModal from '@/components/molecules/SimpleModal.vue';
import { CustomerCoupon as TypeCustomerCoupon } from '@/repositories/couponRepository';
import { Shop } from '@/repositories/shopRepository';
import { PropType } from 'vue';
import { useData } from '@/compositions/useData';
import { useShop } from '@/compositions/useShop';
import CouponPrice from '@/components/molecules/coupon/CouponPrice.vue';
import { useRoute, useRouter } from 'vue-router/composables';

// 店舗情報を管理するためのキー
const SHOP_DETAILS_KEY = 'shopDetails';

export default defineComponent({
  name: 'CustomerCoupon',

  props: {
    customerCoupon: {
      type: Object as PropType<TypeCustomerCoupon>,
      required: true,
    },
    // CS-9349 customerCoupon.contents.shopIdに羽位されるまでラグが存在するため、直接引き回す
    shopId: {
      type: String,
      required: false,
    },
    targetCouponId: {
      type: String,
      default: null,
    },
  },

  components: { DialogCustomerCouponDetail, SimpleModal, CouponPrice },

  setup(props, { emit }) {
    const { get, set } = useData();
    const { getDetails } = useShop();
    const isShopNameModalActive = ref(false);
    const { query } = useRoute();
    const router = useRouter();

    const showShopNameDetail = () => {
      isShopNameModalActive.value = true;
    };

    const state = reactive({
      used: props.customerCoupon.used,
      usedAt: datetimeFormat(props.customerCoupon.updatedAt, 'datetime'),
    });

    const name = computed(() => props.customerCoupon.couponMaster.name);

    const overview = computed(() => {
      try {
        return JSON.parse(props.customerCoupon.couponMaster.attrs).overview || null;
      } catch {
        return null;
      }
    });

    const mainImageUrl = computed(() => {
      try {
        return JSON.parse(props.customerCoupon.couponMaster.attrs).mainImageUrl || null;
      } catch {
        return null;
      }
    });

    /**
     * CS-9138 画像アスペクト比を変更可能に
     */
    const imageAspect = computed(() => {
      try {
        const str = JSON.parse(props.customerCoupon.couponMaster.attrs);
        const aspect = str.imageAspect || '16 / 9';
        return aspect.replace(' / ', 'by');
      } catch {
        return '16by9';
      }
    });

    const discount = computed(() => JSON.parse(props.customerCoupon.couponMaster.attrs).discount);

    const hasDiscount = computed(() => {
      if (discount.value.type === 'FREE') return false;
      if (discount.value.type === 'CONST' && discount.value.value === 0) return false;
      if (discount.value.type === 'RATE' && discount.value.value === 0) return false;
      return true;
    });

    const expiresAt = computed(() => {
      // 動的有効期限
      if (props.customerCoupon.expiresAt) return datetimeFormat(props.customerCoupon.expiresAt);

      // 静的有効期限
      const expires = JSON.parse(props.customerCoupon.couponMaster.attrs).expires;
      if (expires.startAt && expires.endAt)
        return `${datetimeFormat(expires.startAt)}<br />〜 ${datetimeFormat(expires.endAt)}`;
      if (expires.startAt) return `${datetimeFormat(expires.startAt)} 〜`;
      if (expires.endAt) return `〜 ${datetimeFormat(expires.endAt)}`;

      return 'なし'; // 有効期限なし
    });

    const expired = computed(() => {
      const { expires: expiresInfo } = JSON.parse(props.customerCoupon.couponMaster.attrs) as {
        expires: { term: number | null; startAt: string | null; endAt: string | null };
      };
      const now = Date.now();
      if (props.customerCoupon.expiresAt) {
        return new Date(props.customerCoupon.expiresAt).getTime() <= now;
      } else if (expiresInfo.endAt) {
        return new Date(expiresInfo.endAt).getTime() <= now;
      }

      return false;
    });

    const hasExpiresAt = computed(() => {
      const expires = JSON.parse(props.customerCoupon.couponMaster.attrs).expires;
      if (expires.term != null && expires.termStartTime) return true;
      if (expires.startAt) return true;
      if (expires.endAt) return true;
      return false;
    });

    const restriction = computed(() => {
      if (!props.customerCoupon.coupon) return undefined;
      if (!props.customerCoupon.coupon.restrictionSettings) return undefined;
      const settings = JSON.parse(props.customerCoupon.coupon.restrictionSettings);
      const { type, value } = settings.customer || {};

      switch (type) {
        case 'ONCE':
          return '１回だけ交換可能';
        case 'DAILY':
          return '毎日1回交換可能' + (value ? `\n(${value}更新)` : '');
        case 'TERM': {
          const termValue: string[] = [value || []].flat();
          return '期間中1回だけ交換可能' + ` ${termValue.map(v => dayjs(v).format('YYYY/MM/DD HH:mm')).join('〜')}`;
        }
        default:
          return undefined;
      }
    });

    /**
     * クーポンの利用店舗を取得する
     */
    const shopDetail = ref<Shop | null>(null);

    // CS-9349: shopDetailがキャッシュにない場合はAPI取得 & キャッシュ保存
    watch(
      () => props.shopId,
      async newShopId => {
        if (!newShopId) {
          shopDetail.value = null;
          return;
        }
        const shopDetails = get(SHOP_DETAILS_KEY) as Map<string, Shop>;
        let detail = shopDetails?.get(newShopId) || null;
        if (!detail) {
          try {
            const shopsFetched = await getDetails([newShopId]);
            if (shopsFetched && shopsFetched.length) {
              detail = shopsFetched[0];
              // キャッシュ更新
              const newMap = shopDetails ? new Map(shopDetails) : new Map<string, Shop>();
              newMap.set(newShopId, detail);
              set(SHOP_DETAILS_KEY, newMap);
            }
          } catch (e) {
            console.error('Failed to fetch shop detail', e);
          }
        }
        shopDetail.value = detail;
      },
      { immediate: true }
    );

    watch(
      () => props.customerCoupon,
      newCustomerCoupon => {
        if (props.shopId) {
          // props.shopId が存在する場合は上の watcher で設定済み
          return;
        }
        if (!newCustomerCoupon || !newCustomerCoupon.contents) {
          shopDetail.value = null;
          return;
        }

        try {
          const contents = JSON.parse(newCustomerCoupon.contents);
          if (!contents.shopId) {
            shopDetail.value = null;
            return;
          }

          const shopDetails = get(SHOP_DETAILS_KEY) as Map<string, Shop>;
          if (!shopDetails) {
            shopDetail.value = null;
            return;
          }

          shopDetail.value = shopDetails.get(contents.shopId) || null;
        } catch (e) {
          console.warn('Failed to parse coupon contents or get shop detail', e);
          shopDetail.value = null;
        }
      },
      { immediate: true }
    );

    /**
     * （明示的に）使用済みスタンプを押す
     * @public
     */
    const stamp = () => {
      state.used = true;
      state.usedAt = datetimeFormat(dayjs().toISOString());
    };

    const dialog = ref(false);

    /**
     * クーポン詳細ダイアログを閉じる
     * @public
     */
    const close = () => {
      dialog.value = false;
    };

    onMounted(() => {
      // CS-9432 該当のクーポン詳細ダイアログを開く
      if (props.targetCouponId === props.customerCoupon.id) {
        dialog.value = true;
      }
    });

    return {
      state,
      name,
      overview,
      mainImageUrl,
      imageAspect, // CS-9138
      discount,
      hasDiscount,
      expiresAt,
      hasExpiresAt,
      expired,
      restriction,
      stamp,

      showShopNameDetail,
      isShopNameModalActive,
      shopDetail,

      // detail dialog
      dialog,
      close,
    };
  },
});
